import React, { useId, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import Text from "@src/lib/ui/Text";
import PlusIcon from "@src/lib/icons/18px/plus";
import { cn } from "@src/lib/utils/cn";
import AgentDialog from "@src/features/agents/AgentDialog";
import AgentGalleryDialog from "@src/features/agents/AgentGalleryDialog";
import AgentAvatar from "@src/features/agents/AgentAvatar";
import AskOpusOrbIcon from "@src/features/assistantChat/AskOpusOrbIcon";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import CaretDownFillIcon from "@src/lib/icons/18px/caret-down-fill";
import {
  OpusAgentFieldsFragment,
  OpusAgentsDocument,
  OpusAgentsQuery,
} from "@src/features/opus-agent/agentPanel.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import {
  ASK_OPUS_AGENT_ID,
  useAgentPanel,
} from "@src/features/opus-agent/agentPanel";
import {
  MANAGER_AGENTS,
  managerAgentAvatar,
  managerAgentPanelId,
} from "@src/features/opus-agent/managerAgents";

type Props = {
  rowClassName: string;
  expanded: boolean;
  onSelect?: () => void;
};

const AdminRailAgentsSection: React.FC<Props> = ({
  rowClassName,
  expanded,
  onSelect,
}) => {
  const {
    gates,
    isOpen,
    selectedAgentId,
    agents,
    archivedAgents,
    openAgent,
    toggleAgent,
  } = useAgentPanel();
  const client = useApolloClient();
  const killSwitchEnabled = useFeatureFlag(Ff.OpusAgentKillSwitch);
  const [showArchived, setShowArchived] = useState(false);
  const archivedListId = useId();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [dialog, setDialog] = useState<{
    agent: OpusAgentFieldsFragment | null;
  } | null>(null);

  if (!gates.askOpus && !gates.agents) return null;

  const openCreated = (agent: OpusAgentFieldsFragment) => {
    client.cache.updateQuery<OpusAgentsQuery>(
      { query: OpusAgentsDocument },
      (data) =>
        data && {
          OpusAgents: [
            ...data.OpusAgents.filter((existing) => existing.id !== agent.id),
            agent,
          ],
        },
    );
    openAgent(agent.id);
  };

  const row = (
    id: string,
    label: string,
    icon: React.ReactNode,
    agent?: OpusAgentFieldsFragment,
  ) => {
    const active = isOpen && selectedAgentId === id;
    const archived = Boolean(agent?.archivedAt);
    return (
      <SidebarMenuItem key={id}>
        <SidebarMenuButton
          isActive={active}
          aria-pressed={active}
          aria-label={label}
          // Avatars read at 20px (the rail's icons are 16px); the tighter gap
          // keeps the names lined up with the other nav labels.
          className={cn(
            rowClassName,
            "gap-3 [&>svg]:!size-5 [&>svg]:!translate-x-0",
            archived && "text-text-tertiary",
          )}
          data-testid={`rail-agent-${id}`}
          data-agent-panel-row={id}
          onClick={() => {
            toggleAgent(id);
            onSelect?.();
          }}
        >
          {icon}
          <span className="truncate">{label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <div className="mt-3 flex w-full flex-col" data-testid="rail-agents">
      {expanded ? (
        <div className="flex h-7 items-center gap-1 pl-1.5">
          <Text variant="fine-print-title" className="text-text-tertiary">
            Agents
          </Text>
          {gates.agents && !killSwitchEnabled && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="New agent"
                  data-testid="rail-new-agent-button"
                  onClick={() => setGalleryOpen(true)}
                >
                  <PlusIcon className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">New agent</TooltipContent>
            </Tooltip>
          )}
        </div>
      ) : (
        <div className="flex h-7 items-center px-1.5" aria-hidden>
          <div className="h-px w-full bg-border-primary" />
        </div>
      )}
      <SidebarMenu>
        {gates.askOpus &&
          row(
            ASK_OPUS_AGENT_ID,
            "Primary Agent",
            <AskOpusOrbIcon size={20} still />,
          )}
        {gates.managerAgents &&
          MANAGER_AGENTS.map((agent) =>
            row(
              managerAgentPanelId(agent.key),
              agent.name,
              <AgentAvatar agent={managerAgentAvatar(agent)} size="icon" />,
            ),
          )}
        {agents.map((agent) =>
          row(
            agent.id,
            agent.name,
            // Shows what the agent is doing, like the Primary Agent orb above it.
            <AgentAvatar agent={agent} size="icon" />,
            agent,
          ),
        )}
      </SidebarMenu>
      {/* The archived rows live under their own header, only while it is
          open. */}
      {expanded && archivedAgents.length > 0 && (
        <button
          type="button"
          aria-expanded={showArchived}
          aria-controls={archivedListId}
          data-testid="rail-agents-show-archived"
          onClick={() => setShowArchived((value) => !value)}
          className="flex h-7 items-center gap-1 rounded-md px-1.5 text-left text-text-tertiary hover:bg-state-hover"
        >
          <CaretDownFillIcon
            className={cn(
              "size-3 transition-transform motion-reduce:transition-none",
              !showArchived && "-rotate-90",
            )}
          />
          <Text variant="fine-print-description" className="text-text-tertiary">
            Archived ({archivedAgents.length})
          </Text>
        </button>
      )}
      {expanded && showArchived && archivedAgents.length > 0 && (
        <SidebarMenu id={archivedListId} data-testid="rail-agents-archived">
          {archivedAgents.map((agent) =>
            row(
              agent.id,
              agent.name,
              // Archived agents rest, dimmed.
              <AgentAvatar agent={agent} size="icon" className="opacity-60" />,
              agent,
            ),
          )}
        </SidebarMenu>
      )}
      {galleryOpen && (
        <AgentGalleryDialog
          open={galleryOpen}
          onOpenChange={setGalleryOpen}
          onCreated={openCreated}
          onCreateFromScratch={() => {
            setGalleryOpen(false);
            setDialog({ agent: null });
          }}
        />
      )}
      {dialog && (
        <AgentDialog
          agent={dialog.agent}
          onClose={() => setDialog(null)}
          onCreated={openCreated}
        />
      )}
    </div>
  );
};

export default AdminRailAgentsSection;
